import { useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { VStack, Wrap, Switch, HStack } from '@chakra-ui/react';

function ToggleableBarChart({
  data = [],
  categories = [],
  seriesKey = 'year',
  chartTitle = '',
  yAxisLabel = '',
  colors = [],
  width = 300,
  height = 300,
}) {
  const [visibleCategories, setVisibleCategories] = useState(
    () => new Set(categories.map((c) => c.key)),
  );

  const toggleCategory = (key) => {
    setVisibleCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const seriesKeys = useMemo(
    () => [...new Set(data.map((item) => item[seriesKey]))],
    [data, seriesKey],
  );

  const activeCategories = categories.filter((c) => visibleCategories.has(c.key));

  const chartCategories = activeCategories.map((c) => c.label);

  const series = seriesKeys.map((key) => ({
    name: key,
    data: activeCategories.map((category) => {
      const row = data.find((item) => item[seriesKey] === key);
      return row?.[category.key] ?? 0;
    }),
  }));

  const maxValue = Math.max(
    ...series.flatMap((s) => s.data),
    0,
  );

  return (
    <HStack spacing={4} align="start">
      <Chart
        options={{
          title: { text: chartTitle },
          chart: { animations: { enabled: false } },
          colors,
          xaxis: {
            categories: chartCategories,
            labels: {
              offsetY: 5,
              rotate: -45,
              rotateAlways: true,
            },
            tickPlacement: 'between',
          },
          yaxis: {
            min: 0,
            title: { text: yAxisLabel },
            labels: {
              formatter(y) {
                if (y === null || y === undefined) return y;

                if (y === 0) return '0%';

                if (maxValue >= 5) {
                  return `${y.toFixed(0)}%`;
                }

                if (maxValue >= 0.5) {
                  return `${y.toFixed(1)}%`;
                }

                return `${y.toFixed(2)}%`;
              },
            },
          },
          tooltip: {
            shared: true,
            intersect: false,
            y: {
              formatter(val) {
                if (val === null || val === undefined) return val;

                if (val >= 0.5) {
                  return `${val.toFixed(1)}%`;
                }

                return `${val.toFixed(2)}%`;
              },
            },
          },
          dataLabels: { enabled: false },
          legend: {
            show: true,
            showForSingleSeries: true,
            offsetY: -10,
          },
        }}
        series={series}
        type="bar"
        width={width}
        height={height}
      />

      <VStack spacing={2} align="start" className="hidden-print">
        {categories.map((category) => (
          <Wrap key={category.key} align="center" spacing={2}>
            <Switch
              size="sm"
              isChecked={visibleCategories.has(category.key)}
              onChange={() => toggleCategory(category.key)}
              fontSize="11px"
              cursor="pointer"
              display="flex"
              alignItems="center"
              isDisabled={!data || data.length === 0}
            >
              {category.label}
            </Switch>
          </Wrap>
        ))}
      </VStack>
    </HStack>
  );
}

export default ToggleableBarChart;
